import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, FileText, Download, X, Mail, Settings, Trash2, Bell, Layout as LayoutIcon, Merge, FileSpreadsheet, Upload } from "lucide-react";
import { TemplatesManagerModal } from "@/components/invoices/InvoiceTemplatesModal";
import SenderManagerModal from "@/components/invoices/SenderManagerModal";
import SendEmailModal from "@/components/invoices/SendEmailModal";
import RemindersModal from "@/components/invoices/RemindersModal";

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="label-mono mb-1">{label}{required && <span className="text-red-600"> *</span>}</div>
    {children}
  </label>
);

const Th = ({ children }) => <th className="text-left px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap">{children}</th>;

const STATUS_COLORS = {
  draft: "bg-zinc-200",
  sent: "bg-blue-200",
  paid: "bg-green-200",
  cancelled: "bg-red-200",
};

export default function Invoices() {
  const [invs, setInvs] = useState([]);
  const [clients, setClients] = useState([]);
  const [senders, setSenders] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showBuild, setShowBuild] = useState(false);
  const [showSenders, setShowSenders] = useState(false);
  const [showReminders, setShowReminders] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [emailFor, setEmailFor] = useState(null);
  const [reminderCount, setReminderCount] = useState(0);
  const [checked, setChecked] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const [showMerge, setShowMerge] = useState(false);
  const [merging, setMerging] = useState(false);

  const load = async () => {
    const [a, b, s, p, t] = await Promise.all([
      api.get("/invoices"),
      api.get("/clients").catch(() => ({ data: [] })),
      api.get("/invoice-senders").catch(() => ({ data: [] })),
      api.get("/invoices/reminders/pending").catch(() => ({ data: { counts: { total: 0 } } })),
      api.get("/invoice-templates").catch(() => ({ data: [] })),
    ]);
    setInvs(a.data);
    setClients(b.data || []);
    setSenders(s.data || []);
    setReminderCount(p.data?.counts?.total || 0);
    setTemplates(t.data || []);
  };
  useEffect(() => { load(); }, []);

  const downloadPdf = async (id, name, templateId) => {
    const token = localStorage.getItem("session_token");
    const params = { ...(token ? { auth: token } : {}), ...(templateId ? { template_id: templateId } : {}) };
    const res = await api.get(`/invoices/${id}/pdf`, { params, responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = u; a.download = `${name}.pdf`; a.click();
    URL.revokeObjectURL(u);
  };

  const downloadExcel = async (id, name) => {
    try {
      const res = await api.get(`/invoices/${id}/excel`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = u; a.download = `${name}.xlsx`; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Excel export failed"); }
  };

  const toggleCheck = (id) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const mergeSelected = async (primaryClientId) => {
    setMerging(true);
    try {
      const { data } = await api.post("/invoices/merge", {
        invoice_ids: [...checked],
        ...(primaryClientId ? { primary_client_id: primaryClientId } : {}),
      });
      toast.success(`Merged ${checked.size} invoices into ${data.invoice_number}`);
      setChecked(new Set()); setShowMerge(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Merge failed"); }
    setMerging(false);
  };

  const deleteInvoice = async (inv) => {
    if (!window.confirm(`Delete invoice ${inv.invoice_number}? This cannot be undone.`)) return;
    try {
      await api.delete(`/invoices/${inv.id}`);
      toast.success("Invoice deleted");
      setChecked((prev) => { const n = new Set(prev); n.delete(inv.id); return n; });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">BILLING</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Invoices</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Drafter · QA1 · QA2 · Final QC — per-job line items</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {checked.size >= 2 && (
            <button onClick={() => setShowMerge(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-700" data-testid="merge-invoices-btn">
              <Merge className="w-4 h-4" /> {`MERGE ${checked.size} CLIENTS`}
            </button>
          )}
          <button onClick={() => setShowImport(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid="invoice-import-btn">
            <Upload className="w-4 h-4" /> IMPORT EXCEL
          </button>
          <button onClick={() => setShowReminders(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2 border-orange-700 text-orange-800 hover:bg-orange-50" data-testid="invoice-reminders-btn">
            <Bell className="w-4 h-4" /> REMINDERS{reminderCount > 0 ? ` (${reminderCount})` : ""}
          </button>
          <button onClick={() => setShowTemplates(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="invoice-templates-btn">
            <LayoutIcon className="w-4 h-4" /> TEMPLATES ({templates.length})
          </button>
          <button onClick={() => setShowSenders(true)} className="brut-btn-secondary px-4 py-2 text-sm flex items-center gap-2" data-testid="invoice-senders-btn">
            <Settings className="w-4 h-4" /> EMAIL SENDERS ({senders.length})
          </button>
          <button onClick={() => setShowBuild(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="invoice-build-btn">
            <Plus className="w-4 h-4" /> BUILD FROM JOBS
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {invs.length === 0 && (
          <div className="brut-card p-10 text-center text-zinc-500 font-mono text-sm lg:col-span-2">
            NO INVOICES YET — BUILD ONE FROM COMPLETED JOBS
          </div>
        )}
        {invs.map((inv) => (
          <div key={inv.id} className="brut-card p-5" data-testid={`invoice-card-${inv.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-2">
                {!inv.is_merged && (
                  <input
                    type="checkbox"
                    className="mt-1 w-4 h-4 accent-indigo-600 cursor-pointer"
                    checked={checked.has(inv.id)}
                    onChange={() => toggleCheck(inv.id)}
                    title="Select to merge with another client's invoice"
                    data-testid={`merge-check-${inv.id}`}
                  />
                )}
                <div>
                  <div className="label-mono">{inv.invoice_number}</div>
                  <div className="font-heading text-xl font-bold mt-1">{inv.client_name}</div>
                  <div className="text-xs font-mono text-zinc-600 mt-1">{inv.period_start} → {inv.period_end}</div>
                  {inv.is_merged && (
                    <span className="inline-block mt-1 font-mono text-[10px] uppercase font-bold px-2 py-0.5 border border-indigo-700 bg-indigo-100 text-indigo-800" data-testid={`merged-badge-${inv.id}`}>
                      MERGED · {(inv.merged_clients || []).length} CLIENTS
                    </span>
                  )}
                </div>
              </div>
              <span className={`font-mono text-xs uppercase font-bold px-2 py-1 border border-zinc-900 ${STATUS_COLORS[inv.status]}`}>{inv.status}</span>
            </div>
            <div className="text-xs font-mono">
              <div>LINES: <b>{inv.line_items?.length || 0}</b></div>
              <div>TOTAL: <b>{({USD:"$",INR:"₹",EUR:"€",GBP:"£"}[inv.currency] || "$")}{inv.total_amount?.toFixed(2)}</b></div>
              {(inv.amount_received || 0) > 0 && inv.status !== "paid" && (
                <div data-testid={`partial-payment-${inv.id}`}>
                  <span className="text-green-700">RECV: <b>{({USD:"$",INR:"₹",EUR:"€",GBP:"£"}[inv.currency] || "$")}{(inv.amount_received || 0).toFixed(2)}</b></span>
                  <span className="text-red-700 ml-2">DUE: <b>{({USD:"$",INR:"₹",EUR:"€",GBP:"£"}[inv.currency] || "$")}{Math.max(inv.balance_due || 0, 0).toFixed(2)}</b></span>
                </div>
              )}
              {inv.total_amount_inr && inv.status === "paid" && (
                <div className="text-green-700">RECEIVED: <b>₹{inv.total_amount_inr.toLocaleString("en-IN")}</b> <span className="text-zinc-500">@ {inv.exchange_rate}</span></div>
              )}
            </div>
            <div className="flex gap-2 mt-4 flex-wrap">
              <button onClick={() => setSelected(inv)} className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`view-invoice-${inv.id}`}>
                <FileText className="w-3.5 h-3.5" /> VIEW
              </button>
              <div className="inline-flex border border-zinc-900 shadow-[2px_2px_0_0_#18181B]">
                <button onClick={() => downloadPdf(inv.id, inv.invoice_number, inv.template_id)} className="bg-yellow-300 px-3 py-1.5 text-xs flex items-center gap-1 font-bold uppercase font-mono hover:bg-yellow-400" data-testid={`pdf-invoice-${inv.id}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                {templates.length > 1 && (
                  <select
                    value={inv.template_id || "default"}
                    onChange={(e) => downloadPdf(inv.id, inv.invoice_number, e.target.value)}
                    className="bg-white px-1 text-[10px] font-mono border-l border-zinc-900 max-w-[120px]"
                    title="Download with a different template"
                    data-testid={`pdf-template-${inv.id}`}
                  >
                    {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                )}
              </div>
              <button onClick={() => downloadExcel(inv.id, inv.invoice_number)} className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 border-emerald-700 text-emerald-800 hover:bg-emerald-50" data-testid={`excel-invoice-${inv.id}`}>
                <FileSpreadsheet className="w-3.5 h-3.5" /> EXCEL
              </button>
              <button onClick={() => setEmailFor(inv)} className="brut-btn-secondary px-3 py-1.5 text-xs flex items-center gap-1 border-blue-700 text-blue-800 hover:bg-blue-50" data-testid={`email-invoice-${inv.id}`}>
                <Mail className="w-3.5 h-3.5" /> SEND EMAIL
              </button>
              {inv.status !== "paid" && (
                <button onClick={() => deleteInvoice(inv)} className="brut-btn-secondary px-2 py-1.5 text-xs flex items-center gap-1 border-red-700 text-red-700 hover:bg-red-50" title="Delete invoice" data-testid={`delete-invoice-${inv.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
              {inv.last_email_sent_at && (
                <span className="font-mono text-[10px] text-zinc-500 self-center" title={`Last sent to ${inv.last_email_to || ""}`}>
                  ✓ sent {inv.last_email_sent_at?.slice(0, 10)}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {showBuild && <BuildModal clients={clients} onClose={() => setShowBuild(false)} onCreated={load} />}
      {showImport && <ImportExcelModal clients={clients} onClose={() => setShowImport(false)} onImported={load} />}
      {showMerge && (
        <MergeModal
          invoices={invs.filter((i) => checked.has(i.id))}
          merging={merging}
          onClose={() => setShowMerge(false)}
          onMerge={mergeSelected}
        />
      )}
      {selected && <DetailModal inv={selected} onClose={() => setSelected(null)} onChanged={load} />}
      {showSenders && <SenderManagerModal senders={senders} onClose={() => setShowSenders(false)} onChanged={load} />}
      {showReminders && (
        <RemindersModal
          senders={senders}
          onClose={() => setShowReminders(false)}
          onSent={load}
          onAddSender={() => { setShowReminders(false); setShowSenders(true); }}
        />
      )}
      {showTemplates && (
        <TemplatesManagerModal templates={templates} onClose={() => setShowTemplates(false)} onChanged={load} />
      )}
      {emailFor && (
        <SendEmailModal
          inv={emailFor}
          senders={senders}
          client={clients.find((c) => c.id === emailFor.client_id) || null}
          onClose={() => setEmailFor(null)}
          onSent={load}
          onAddSender={() => { setEmailFor(null); setShowSenders(true); }}
        />
      )}
    </div>
  );
}

function BuildModal({ clients, onClose, onCreated }) {
  const [clientId, setClientId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [pricingMode, setPricingMode] = useState("per_job");

  const selectedClient = clients.find((c) => c.id === clientId);

  const build = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error("Select a client"); return; }
    try {
      const fd = new FormData();
      fd.append("client_id", clientId);
      fd.append("start", start); fd.append("end", end);
      fd.append("pricing_mode", pricingMode);
      await api.post("/invoices/from-jobs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Invoice built — review and edit line items");
      onCreated(); onClose();
    } catch { toast.error("Build failed"); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl font-black mb-2">Build Invoice From Jobs</h2>
        <p className="text-xs font-mono text-zinc-600 mb-4">Includes jobs with status <b>completed</b> or <b>sent_to_client</b> updated in the date range. Pricing auto-calculated from client defaults; per-job rate_override takes precedence.</p>
        <form onSubmit={build} className="space-y-3">
          <Field label="CLIENT" required>
            <select required value={clientId} onChange={(e) => setClientId(e.target.value)} className="brut-input w-full px-3 py-2 text-sm" data-testid="build-client-select">
              <option value="">— select client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          {selectedClient && (
            <div className="border border-zinc-900 bg-zinc-50 p-2 text-xs font-mono space-y-0.5">
              <div>PER JOB: <b>{selectedClient.currency} {selectedClient.default_rate_per_job || 0}</b></div>
              <div>PER HOUR: <b>{selectedClient.currency} {selectedClient.default_rate_per_hour || 0}</b></div>
              {selectedClient.team && <div>TEAM: <b>{selectedClient.team}</b></div>}
            </div>
          )}
          <Field label="PRICING MODE">
            <select value={pricingMode} onChange={(e) => setPricingMode(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono">
              <option value="per_job">Per Job Flat Rate</option>
              <option value="per_hour">Per Hour × Hours</option>
            </select>
          </Field>
          <Field label="PERIOD START"><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="PERIOD END"><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
            <button type="submit" className="brut-btn px-4 py-2 text-sm" data-testid="build-invoice-submit">BUILD INVOICE</button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

function MergeModal({ invoices, merging, onClose, onMerge }) {
  const withClient = invoices.filter((i) => i.client_id);
  const [primary, setPrimary] = useState(withClient[0]?.client_id || "");

  return createPortal(
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-heading text-2xl font-black mb-2">Merge {invoices.length} Invoices</h2>
        <p className="text-xs font-mono text-zinc-600 mb-4">Pick which client's details (contact, address, GSTIN) appear as <b>BILL TO</b> on the merged invoice. All jobs from every client are still listed with per-client subtotals.</p>
        <div className="space-y-2 mb-4 max-h-64 overflow-y-auto">
          {invoices.map((inv) => (
            <label key={inv.id} className={`flex items-start gap-3 border border-zinc-900 p-3 cursor-pointer ${primary && primary === inv.client_id ? "bg-indigo-50 shadow-[2px_2px_0_0_#4F46E5]" : "bg-white"} ${!inv.client_id ? "opacity-60" : ""}`}>
              <input
                type="radio"
                name="primary-client"
                className="mt-0.5 w-4 h-4 accent-indigo-600"
                disabled={!inv.client_id}
                checked={!!primary && primary === inv.client_id}
                onChange={() => setPrimary(inv.client_id)}
                data-testid={`primary-client-radio-${inv.id}`}
              />
              <div className="flex-1">
                <div className="font-heading font-bold text-sm">{inv.client_name}</div>
                <div className="text-[10px] font-mono text-zinc-600">{inv.invoice_number} · {inv.line_items?.length || 0} jobs · {({USD:"$",INR:"₹",EUR:"€",GBP:"£"}[inv.currency] || "$")}{(inv.total_amount || 0).toFixed(2)}</div>
                {!inv.client_id && <div className="text-[10px] font-mono text-orange-700">No client record linked — details unavailable</div>}
              </div>
              {primary && primary === inv.client_id && (
                <span className="font-mono text-[9px] uppercase font-bold px-1.5 py-0.5 border border-indigo-700 bg-indigo-100 text-indigo-800">DEFAULT DETAILS</span>
              )}
            </label>
          ))}
        </div>
        {withClient.length > 0 && (
          <label className="flex items-center gap-2 mb-4 text-xs font-mono cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-zinc-900" checked={!primary} onChange={(e) => setPrimary(e.target.checked ? "" : (withClient[0]?.client_id || ""))} data-testid="no-primary-client-check" />
            Don't keep any single client's details — show combined client names only
          </label>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button onClick={() => onMerge(primary || null)} disabled={merging} className="brut-btn px-4 py-2 text-sm bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60" data-testid="confirm-merge-btn">
            {merging ? "MERGING…" : "MERGE INVOICES"}
          </button>
        </div>
      </div>
    </div>
  , document.body);
}

function ImportExcelModal({ clients, onClose, onImported }) {
  const [clientId, setClientId] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/invoices/import-template", { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a"); a.href = u; a.download = "invoice_import_template.xlsx"; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Template download failed"); }
  };

  const buildForm = (isPreview) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("client_id", clientId);
    fd.append("period_start", start);
    fd.append("period_end", end);
    if (isPreview) fd.append("preview", "true");
    return fd;
  };

  const doPreview = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error("Select a client"); return; }
    if (!file) { toast.error("Choose an Excel/CSV file"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/import-excel", buildForm(true), { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Preview failed"); }
    setBusy(false);
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/invoices/import-excel", buildForm(false), { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported_rows} line items → ${data.invoice.invoice_number}`);
      onImported(); onClose();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    setBusy(false);
  };

  const sym = preview ? ({ USD: "$", INR: "₹", EUR: "€", GBP: "£" }[preview.currency] || "") : "";
  return createPortal(
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className={`brut-card w-full flex flex-col min-h-0 ${preview ? "max-w-3xl" : "max-w-md"}`} style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="px-6 pt-5 shrink-0">
          <h2 className="font-heading text-2xl font-black mb-2">Import Invoice From Excel</h2>
        </div>
        {!preview ? (
          <div className="px-6 pb-6 overflow-y-auto">
            <p className="text-xs font-mono text-zinc-600 mb-3">Upload the SD-format Excel (org_initials → hours). Amounts are auto-calculated from the client's default per-project or per-hour rate.</p>
            <button type="button" onClick={downloadTemplate} className="brut-btn-secondary px-3 py-1.5 text-xs mb-4 flex items-center gap-1" data-testid="download-import-template-btn">
              <Download className="w-3.5 h-3.5" /> DOWNLOAD TEMPLATE
            </button>
            <form onSubmit={doPreview} className="space-y-3">
              <Field label="CLIENT" required>
                <select required value={clientId} onChange={(e) => setClientId(e.target.value)} className="brut-input w-full px-3 py-2 text-sm" data-testid="import-client-select">
                  <option value="">— select client —</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label="PERIOD START" required><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
              <Field label="PERIOD END" required><input required type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
              <Field label="EXCEL / CSV FILE" required>
                <input required type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="brut-input w-full px-3 py-2 text-sm" data-testid="import-file-input" />
              </Field>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
                <button type="submit" disabled={busy} className="brut-btn px-4 py-2 text-sm disabled:opacity-60" data-testid="preview-import-btn">{busy ? "PARSING…" : "PREVIEW"}</button>
              </div>
            </form>
          </div>
        ) : (
          <>
            <div className="px-6 shrink-0">
              <div className="border border-zinc-900 bg-amber-50 p-2.5 font-mono text-xs flex flex-wrap gap-x-5 gap-y-1 mb-3" data-testid="import-preview-summary">
                <span>CLIENT: <b>{preview.client_name}</b></span>
                <span>ROWS: <b>{preview.rows}</b></span>
                <span>PRICING: <b>{preview.pricing_mode === "flat_per_job" ? `FLAT ${sym}${preview.rate_per_job}/JOB` : preview.pricing_mode === "per_hour" ? `${sym}${preview.rate_per_hour}/HOUR` : "FROM FILE"}</b></span>
                <span>TOTAL: <b className="text-green-800" data-testid="import-preview-total">{sym}{preview.total_amount.toFixed(2)}</b></span>
              </div>
            </div>
            <div className="overflow-y-auto flex-1 min-h-0 px-6">
              <table className="w-full text-xs border-collapse">
                <thead className="sticky top-0">
                  <tr className="bg-zinc-900 text-white">
                    {["Date", "Job #", "Property", "Hours", "Per Hr", "Flat", "Amount"].map((h) => <th key={h} className="text-left px-2 py-1.5 label-mono text-white">{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {preview.items.map((li) => (
                    <tr key={li._key} className="border-b border-zinc-200">
                      <td className="px-2 py-1 font-mono">{li.date}</td>
                      <td className="px-2 py-1 font-mono">{li.job_number}</td>
                      <td className="px-2 py-1 font-mono truncate max-w-[220px]">{li.project_name}</td>
                      <td className="px-2 py-1 font-mono">{li.hours}</td>
                      <td className="px-2 py-1 font-mono">{li.per_hours || ""}</td>
                      <td className="px-2 py-1 font-mono">{li.flat_price || ""}</td>
                      <td className="px-2 py-1 font-mono font-bold">{sym}{(li.amount || 0).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 shrink-0">
              <button type="button" onClick={() => setPreview(null)} className="brut-btn-secondary px-4 py-2 text-sm" data-testid="back-import-btn">BACK</button>
              <button type="button" onClick={confirmImport} disabled={busy} className="brut-btn px-4 py-2 text-sm bg-green-700 text-white hover:bg-green-800 disabled:opacity-60" data-testid="import-invoice-submit">{busy ? "IMPORTING…" : `CONFIRM IMPORT (${sym}${preview.total_amount.toFixed(2)})`}</button>
            </div>
          </>
        )}
      </div>
    </div>
  , document.body);
}

function DetailModal({ inv, onClose, onChanged }) {
  // Assign stable client-side keys for React list rendering (lines have no DB id)
  const [items, setItems] = useState(() =>
    (inv.line_items || []).map((li, i) => ({ ...li, _key: li._key || `li_${Date.now()}_${i}` }))
  );
  const [status, setStatus] = useState(inv.status);
  const [payments, setPayments] = useState(inv.payments || []);
  const [payAmt, setPayAmt] = useState("");
  const [payDate, setPayDate] = useState(new Date().toISOString().slice(0, 10));
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [showPaidModal, setShowPaidModal] = useState(false);
  const [exchangeRate, setExchangeRate] = useState(83.0);
  const [refreshing, setRefreshing] = useState(false);

  const sdRows = items.filter((li) => li.surveydrafts_id).length;
  const sdRowsWithHours = items.filter((li) => li.hours_source === "surveydrafts" && (li.hours || 0) > 0).length;
  const sdTotalHours = items
    .filter((li) => li.hours_source === "surveydrafts")
    .reduce((a, li) => a + (parseFloat(li.hours) || 0), 0);

  const refreshSdHours = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post(`/invoices/${inv.id}/refresh-sd-hours`);
      if (data?.ok) {
        toast.success(`Warmed ${data.warmed} SD jobs · updated ${data.updated_rows} line items`, { duration: 7000 });
        // Mirror server-side items back into local state
        const fresh = data.invoice.line_items || [];
        setItems(fresh.map((li, i) => ({ ...li, _key: li._key || `li_${Date.now()}_${i}` })));
        onChanged && onChanged();
      } else {
        toast.error(data?.reason || "Refresh failed");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Refresh failed"); }
    setRefreshing(false);
  };

  const updateItem = (idx, key, value) => {
    const next = [...items];
    next[idx] = { ...next[idx], [key]: key === "amount" || key === "hours" ? (parseFloat(value) || 0) : value };
    setItems(next);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const addItem = () => setItems([...items, { _key: `li_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`, date: "", job_number: "", project_name: "", drafter: "", qa1: "", qa2: "", final_qc: "", status: "completed", hours: 0, amount: 0 }]);

  // When status changes to "paid" for non-INR invoices, prompt for exchange rate
  const handleStatusChange = (newStatus) => {
    if (newStatus === "paid" && (inv.currency || "USD") !== "INR" && !inv.total_amount_inr) {
      setShowPaidModal(true);
    } else {
      setStatus(newStatus);
    }
  };

  const confirmPaid = async () => {
    try {
      await api.patch(`/invoices/${inv.id}`, { line_items: items, status: "paid", exchange_rate: exchangeRate });
      toast.success(`Invoice marked as paid — converted at ${exchangeRate} INR/${inv.currency}`);
      setShowPaidModal(false);
      onChanged(); onClose();
    } catch { toast.error("Save failed"); }
  };

  const save = async () => {
    try {
      await api.patch(`/invoices/${inv.id}`, { line_items: items, status });
      toast.success("Saved");
      onChanged(); onClose();
    } catch { toast.error("Save failed"); }
  };

  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const sym = { USD: "$", INR: "₹", EUR: "€", GBP: "£" }[inv.currency] || "";
  const received = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const balance = total - received;

  const addPayment = async () => {
    const amt = parseFloat(payAmt);
    if (!amt || amt <= 0) { toast.error("Enter a payment amount"); return; }
    try {
      const { data } = await api.post(`/invoices/${inv.id}/payments`, {
        amount: amt, date: payDate, method: payMethod, reference: payRef,
      });
      setPayments(data.invoice.payments || []);
      setStatus(data.invoice.status);
      setPayAmt(""); setPayRef("");
      toast.success(data.invoice.status === "paid" ? "Payment recorded — invoice fully PAID" : "Payment recorded");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Payment failed"); }
  };

  const removePayment = async (pid) => {
    try {
      const { data } = await api.delete(`/invoices/${inv.id}/payments/${pid}`);
      setPayments(data.invoice.payments || []);
      setStatus(data.invoice.status);
      toast.success("Payment removed");
      onChanged && onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || "Remove failed"); }
  };

  const downloadReceipt = async (p) => {
    try {
      const res = await api.get(`/invoices/${inv.id}/payments/${p.id}/receipt`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = u; a.download = `RCPT-${p.id.replace("pay_", "").toUpperCase()}.pdf`; a.click();
      URL.revokeObjectURL(u);
    } catch { toast.error("Receipt download failed"); }
  };

  return createPortal(
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-6xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="label-mono">{inv.invoice_number}</div>
            <h2 className="font-heading text-2xl font-black">{inv.client_name}</h2>
            <div className="text-xs font-mono text-zinc-600">{inv.period_start} → {inv.period_end}</div>
          </div>
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => handleStatusChange(e.target.value)} className="brut-input px-3 py-2 text-sm font-mono">
              <option value="draft">DRAFT</option>
              <option value="sent">SENT</option>
              <option value="paid">PAID</option>
              <option value="cancelled">CANCELLED</option>
            </select>
            <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>

        <div className="overflow-x-auto border border-zinc-900">
          <table className="w-full text-xs border-collapse">
            <thead>
              <tr className="bg-zinc-900 text-white">
                {inv.is_merged && <Th>Client</Th>}<Th>Date</Th><Th>Job #</Th><Th>Project</Th><Th>Drafter</Th><Th>QA/QC 1</Th><Th>QA/QC 2</Th><Th>Final QC</Th><Th>Status</Th><Th>Hours</Th><Th>Src</Th><Th>Amount</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((li, idx) => (
                <tr key={li._key} className="border-b border-zinc-200">
                  {inv.is_merged && <td className="px-1 font-mono text-[10px] font-bold whitespace-nowrap">{li.client_name || ""}</td>}
                  <td className="px-1"><input className="w-24 px-1 py-1 font-mono text-xs" value={li.date} onChange={(e) => updateItem(idx, "date", e.target.value)} /></td>
                  <td className="px-1"><input className="w-20 px-1 py-1 font-mono text-xs" value={li.job_number} onChange={(e) => updateItem(idx, "job_number", e.target.value)} /></td>
                  <td className="px-1"><input className="w-40 px-1 py-1 text-xs" value={li.project_name} onChange={(e) => updateItem(idx, "project_name", e.target.value)} /></td>
                  <td className="px-1"><input className="w-24 px-1 py-1 text-xs" value={li.drafter} onChange={(e) => updateItem(idx, "drafter", e.target.value)} /></td>
                  <td className="px-1"><input className="w-24 px-1 py-1 text-xs" value={li.qa1} onChange={(e) => updateItem(idx, "qa1", e.target.value)} /></td>
                  <td className="px-1"><input className="w-24 px-1 py-1 text-xs" value={li.qa2} onChange={(e) => updateItem(idx, "qa2", e.target.value)} /></td>
                  <td className="px-1"><input className="w-24 px-1 py-1 text-xs" value={li.final_qc} onChange={(e) => updateItem(idx, "final_qc", e.target.value)} /></td>
                  <td className="px-1">
                    <select className="w-28 px-1 py-1 text-xs font-mono" value={li.status} onChange={(e) => updateItem(idx, "status", e.target.value)}>
                      <option value="completed">COMPLETED</option>
                      <option value="hold">HOLD</option>
                      <option value="sent_to_client">SENT</option>
                      <option value="cancelled">CANCELLED</option>
                    </select>
                  </td>
                  <td className="px-1"><input type="number" className="w-16 px-1 py-1 font-mono text-xs" value={li.hours} onChange={(e) => updateItem(idx, "hours", e.target.value)} /></td>
                  <td className="px-1 text-center">
                    {li.hours_source === "surveydrafts" ? (
                      <span title={`Pulled from ${li.sd_sessions || 0} surveydrafts work session(s)`} className="px-1 py-0.5 border border-zinc-900 bg-emerald-100 text-[9px] font-mono font-bold inline-flex items-center gap-0.5">
                        SD · {li.sd_sessions || 0}s
                      </span>
                    ) : li.hours_source === "land_drafters" ? (
                      <span title="Land Drafters local taken_time_hours" className="px-1 py-0.5 border border-zinc-300 bg-zinc-50 text-[9px] font-mono">LD</span>
                    ) : (
                      <span className="text-[9px] text-zinc-400 font-mono">—</span>
                    )}
                  </td>
                  <td className="px-1"><input type="number" className="w-20 px-1 py-1 font-mono text-xs" value={li.amount} onChange={(e) => updateItem(idx, "amount", e.target.value)} /></td>
                  <td className="px-1"><button onClick={() => removeItem(idx)} className="text-red-600 px-1">✕</button></td>
                </tr>
              ))}
              <tr className="bg-yellow-100 font-bold">
                <td colSpan={inv.is_merged ? 11 : 10} className="px-3 py-2 text-right font-mono">TOTAL</td>
                <td className="px-3 py-2 font-mono">{sym}{total.toFixed(2)}</td>
                <td></td>
              </tr>
              {inv.total_amount_inr && inv.status === "paid" && (
                <tr className="bg-green-100">
                  <td colSpan={inv.is_merged ? 11 : 10} className="px-3 py-2 text-right font-mono text-xs">PAID IN INR @ {inv.exchange_rate}</td>
                  <td className="px-3 py-2 font-mono font-bold text-green-900">₹{inv.total_amount_inr?.toLocaleString("en-IN")}</td>
                  <td></td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 border border-zinc-900 p-3 bg-zinc-50" data-testid="payments-section">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <div className="label-mono">PAYMENTS · {payments.length}</div>
            <div className="font-mono text-xs flex gap-4">
              <span>RECEIVED: <b className="text-green-700" data-testid="amount-received">{sym}{received.toFixed(2)}</b></span>
              <span>BALANCE DUE: <b className={balance > 0.005 ? "text-red-700" : "text-green-700"} data-testid="balance-due">{sym}{Math.max(balance, 0).toFixed(2)}</b></span>
            </div>
          </div>
          {payments.length > 0 && (
            <table className="w-full text-xs border-collapse mb-2">
              <tbody>
                {payments.map((p) => (
                  <tr key={p.id} className="border-b border-zinc-200" data-testid={`payment-row-${p.id}`}>
                    <td className="py-1 font-mono">{p.date}</td>
                    <td className="py-1 font-mono uppercase text-[10px]">{(p.method || "").replace("_", " ")}</td>
                    <td className="py-1 font-mono text-zinc-500">{p.reference}</td>
                    <td className="py-1 font-mono font-bold text-right">{sym}{(parseFloat(p.amount) || 0).toFixed(2)}</td>
                    <td className="py-1 text-right whitespace-nowrap">
                      <button onClick={() => downloadReceipt(p)} className="text-blue-700 px-1" title="Download receipt PDF" data-testid={`receipt-payment-${p.id}`}>⤓</button>
                      <button onClick={() => removePayment(p.id)} className="text-red-600 px-1" title="Remove payment" data-testid={`remove-payment-${p.id}`}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            <input type="number" step="0.01" placeholder={`Amount (${sym})`} className="brut-input px-2 py-1.5 text-xs font-mono w-28" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="payment-amount-input" />
            <input type="date" className="brut-input px-2 py-1.5 text-xs font-mono" value={payDate} onChange={(e) => setPayDate(e.target.value)} data-testid="payment-date-input" />
            <select className="brut-input px-2 py-1.5 text-xs font-mono" value={payMethod} onChange={(e) => setPayMethod(e.target.value)} data-testid="payment-method-select">
              <option value="bank_transfer">BANK TRANSFER</option>
              <option value="wire">WIRE</option>
              <option value="upi">UPI</option>
              <option value="cheque">CHEQUE</option>
              <option value="cash">CASH</option>
              <option value="other">OTHER</option>
            </select>
            <input placeholder="Reference / UTR" className="brut-input px-2 py-1.5 text-xs font-mono w-36" value={payRef} onChange={(e) => setPayRef(e.target.value)} data-testid="payment-reference-input" />
            <button onClick={addPayment} className="brut-btn px-3 py-1.5 text-xs bg-green-700 text-white hover:bg-green-800" data-testid="add-payment-btn">+ RECORD PAYMENT</button>
          </div>
        </div>

        <div className="flex justify-between mt-4">
          <button onClick={addItem} className="brut-btn-secondary px-3 py-1.5 text-xs">+ ADD LINE</button>
          <div className="flex gap-2">
            {sdRows > 0 && (
              <button
                onClick={refreshSdHours}
                disabled={refreshing}
                className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1 border-emerald-700 text-emerald-800 hover:bg-emerald-50 disabled:opacity-60"
                data-testid="refresh-sd-hours-btn"
                title="Re-fetch work_sessions from surveydrafts.us and recompute hours + amounts"
              >
                {refreshing ? "REFRESHING…" : `↻ REFRESH SD HOURS (${sdRowsWithHours}/${sdRows} · ${sdTotalHours.toFixed(1)}h)`}
              </button>
            )}
            <button onClick={save} className="brut-btn px-4 py-2 text-sm" data-testid="save-invoice-btn">SAVE INVOICE</button>
          </div>
        </div>
      </div>

      {showPaidModal && (
        <div className="fixed inset-0 bg-zinc-900/60 z-[60] flex items-center justify-center p-2 sm:p-4" onClick={() => setShowPaidModal(false)}>
          <div className="brut-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-heading text-xl font-black mb-2">Mark as Paid — Convert to INR</h3>
            <p className="text-xs font-mono text-zinc-600 mb-4">Invoice total <b>{sym}{total.toFixed(2)}</b> ({inv.currency}) will be converted to INR using the rate below.</p>
            <label className="block mb-3">
              <div className="label-mono mb-1">EXCHANGE RATE (1 {inv.currency} = ? INR)</div>
              <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono" value={exchangeRate} onChange={(e) => setExchangeRate(parseFloat(e.target.value) || 0)} data-testid="exchange-rate-input" />
            </label>
            <div className="bg-yellow-100 border border-zinc-900 p-3 font-mono text-sm mb-4">
              <div>BILLED: <b>{sym}{total.toFixed(2)}</b></div>
              <div>RECEIVED: <b className="text-green-700">₹{(total * exchangeRate).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</b></div>
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPaidModal(false)} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
              <button onClick={confirmPaid} className="brut-btn px-4 py-2 text-sm" data-testid="confirm-paid-btn">CONFIRM PAID</button>
            </div>
          </div>
        </div>
      )}
    </div>
  , document.body);
}

